import { SectionHeader } from "@/components/common/section-components/SectionHeader";
import { InputField } from "@/components/input-field/InputField";
import { AppRoutes } from "@/constants/routes";
import { AppColors } from "@/styles/colors";
import { isEmpty } from "@/utils/query-string";
import { Card, Flex, Grid, GridItem } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Profession,
  relationFieldSchema,
} from "../schema/relation-fields.schema";
import { getProfesionesSimilares } from "@/services/relation-fields.service";
import { SimilarRelationFieldsAlert } from "@/components/common/section-components/alert/SimilarRelationFieldsAlert";
import { handleCreateProfessionHandler } from "../schema/handler-functions";
import { useRoles } from "@/hooks/useRoles";

export const AddProfessionPage = () => {
  const { control } = useForm({
    resolver: zodResolver(relationFieldSchema),
  });

  const watchedValues = useWatch({
    control,
  });

  const hasNoFilledValues = useMemo(() => {
    const { nombre } = watchedValues || {};

    return isEmpty(nombre);
  }, [watchedValues]);

  const [loadingCreateProfession, setLoadingCreateProfession] =
    useState<boolean>(false);
  const navigate = useNavigate();

  const handleCancel = () => {
    navigate(AppRoutes.RELATION_FIELDS);
  };

  const { fetchPermissions } = useRoles();

  const [similarProfessions, setSimilarProfessions] = useState<
    Profession[] | undefined
  >([]);

  const handleCreateHobbie = () => {
    handleCreateProfessionHandler({
      setLoadingCreateProfession,
      navigate,
      watchedValues,
      fetchPermissions,
    });
  };

  const [loadingSimilarProfessions, setLoadingSimilarProfessions] =
    useState<boolean>(false);

  const getSimilarHobbies = async (nombre: string) => {
    setLoadingSimilarProfessions(true);
    const response = await getProfesionesSimilares({
      nombre,
    });

    setSimilarProfessions(response);

    setLoadingSimilarProfessions(false);
  };

  useEffect(() => {
    const { nombre } = watchedValues || {};
    if (!nombre) return;

    const timeout = setTimeout(() => {
      getSimilarHobbies(nombre);
    }, 500);

    return () => clearTimeout(timeout);
  }, [watchedValues?.nombre]);

  return (
    <Flex flexDir="column" paddingTop="20px" gap="12px">
      <SectionHeader
        title="Agregar profesión"
        addButtonLabel="Agregar"
        cancelButtonLabel="Cancelar"
        handleCancel={handleCancel}
        handleAdd={handleCreateHobbie}
        addButtonType="submit"
        disableAdd={hasNoFilledValues || loadingCreateProfession}
        loading={loadingCreateProfession}
      />

      <Card.Root maxW="full" marginTop="30px" borderRadius="8px">
        <Card.Body
          paddingX="48px"
          paddingY="30px"
          borderRadius="8px"
          border={`1px solid ${AppColors.WHITE_BORDER_1}`}
        >
          <Grid templateColumns="repeat(3, 1fr)">
            <GridItem paddingX="16px" paddingY="8px">
              <InputField
                inputName="nombre"
                labelText="Nombre"
                control={control}
                disabled={loadingCreateProfession}
                placeholder="Ingresar nombre de la profesión"
              />
            </GridItem>
          </Grid>
        </Card.Body>
      </Card.Root>

      {/* Profesiones similares */}
      {Boolean(similarProfessions?.length) &&
        !isEmpty(watchedValues?.nombre) && (
          <SimilarRelationFieldsAlert
            title="Hay profesiones similares existentes"
            legend="Evite repeticiones"
            similarItems={similarProfessions?.map((el) => el.nombre) || []}
            loadingSimilarElements={loadingSimilarProfessions}
          />
        )}
    </Flex>
  );
};
